import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from '@seller/shared';
import {
  api,
  ApiError,
  queryString,
  type BookingCatalogRow,
  type BookingMatchDecision,
  type BookingMatchState,
  type BookingRematchResult,
  type BookingReviewResult,
  type BookingReviewRow,
  type BookingView,
  type UploadBookingResult,
} from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  TableScroll,
  Tabs,
} from '../components/ui.js';
import { ProductThumb } from '../components/ProductThumb.js';
import { CompareFilledSheet } from './bookings/CompareFilledSheet.js';
import { day, integer, money } from '../format.js';

/**
 * Order bookings: upload a supplier season booking workbook, browse its catalog,
 * and tie each line to a seller SKU on the match-review tab. A booking selector
 * switches between uploaded workbooks. Reading is open to any signed-in account;
 * uploading and recording a match need the import permission. See docs/bookings.md.
 */

const CATEGORY_LABELS: Record<string, string> = {
  racquet: 'Racquet',
  demo_racquet: 'Demo racquet',
  string: 'String',
  grip: 'Grip',
  bag: 'Bag',
  footwear: 'Footwear',
};

const WINDOW_LABELS: Record<string, string> = {
  ship_1: '1st ship',
  ship_2: '2nd ship',
  ship_3: '3rd ship',
  q2: 'Q2',
  q3: 'Q3',
  q4: 'Q4',
};

const AVAILABILITY_LABELS: Record<string, string> = {
  available: 'Available',
  not_available: 'Not available',
  prebook: 'Pre-book',
  unknown: 'Unknown',
};

export function BookingsPage() {
  const session = useSession();
  const canWrite = session.status === 'signedIn' && can(session.user.role, 'data:import');

  const bookings = useQuery({
    queryKey: ['bookings'],
    queryFn: () => api.get<{ bookings: BookingView[] }>('/api/bookings'),
  });

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const list = bookings.data?.bookings ?? [];
  const selected = list.find((b) => b.id === (selectedId ?? list[0]?.id)) ?? null;
  const [tab, setTab] = useState<'catalog' | 'review'>('catalog');

  // The active booking (from planning settings) drives the filled-workbook
  // download: it fills whichever booking the purchase plan is placed against.
  const settings = useQuery({
    queryKey: ['planning-settings'],
    queryFn: () => api.get<{ activeBookingId: number | null }>('/api/planning/settings'),
  });
  const activeBookingId = settings.data?.activeBookingId ?? null;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Order bookings"
        description="Upload a supplier season booking workbook, browse what is orderable, and match each line to one of your seller SKUs."
      />

      {canWrite && <UploadCard onUploaded={(id) => setSelectedId(id)} />}

      {bookings.isPending && <Spinner label="Loading bookings" />}
      {bookings.isError && <Alert>{bookings.error.message}</Alert>}

      {bookings.data && list.length === 0 && (
        <Card className="p-6">
          <p className="text-sm text-muted">
            No bookings yet.{' '}
            {canWrite
              ? 'Upload a supplier booking workbook above.'
              : 'Ask an importer to upload one.'}
          </p>
        </Card>
      )}

      {selected && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-64">
              <Field label="Booking">
                <Select
                  value={String(selected.id)}
                  onChange={(e) => setSelectedId(Number(e.target.value))}
                >
                  {list.map((b) => (
                    <option key={b.id} value={String(b.id)}>
                      {b.name} ({b.bookingYear}) — {integer(b.lineCount)} lines
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <p className="text-xs text-muted">
              {selected.sheetNames.length} tabs · uploaded {day(selected.uploadedAt)}
              {selected.uploadedByName ? ` by ${selected.uploadedByName}` : ''}
            </p>
            {canWrite && <RematchButton bookingId={selected.id} />}
            {activeBookingId === selected.id ? (
              <a
                href="/api/purchase-plan-booking.xlsx"
                className="inline-flex items-center rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-brand-on hover:bg-brand-700"
                title="A copy of this workbook with the purchase plan's funded quantities written into the grid."
              >
                Download filled workbook
              </a>
            ) : (
              <Link
                to="/purchase-plan"
                className="text-xs text-brand-600 hover:underline"
                title="Set this booking active on the purchase plan to fill and download it."
              >
                {activeBookingId === null
                  ? 'Set active on the purchase plan to fill it'
                  : 'Another booking is active on the plan'}
              </Link>
            )}
          </div>

          {selected.warnings.length > 0 && (
            <Alert tone="warning">
              <span className="font-medium">Parse notes</span>
              <ul className="mt-1 list-disc pl-5">
                {selected.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* A stopgap for the 2026 holiday order; see bookings/CompareFilledSheet.tsx. */}
          <CompareFilledSheet key={selected.id} bookingId={selected.id} canWrite={canWrite} />

          <Tabs
            items={[
              { id: 'catalog', label: 'Catalog' },
              { id: 'review', label: 'Match review' },
            ]}
            active={tab}
            onChange={(id) => setTab(id as 'catalog' | 'review')}
          />

          {tab === 'catalog' ? (
            <CatalogPanel bookingId={selected.id} />
          ) : (
            <ReviewPanel bookingId={selected.id} canWrite={canWrite} />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Re-match
// ---------------------------------------------------------------------------

/**
 * Recomputes the cached match proposals for the booking's undecided lines
 * against the current SKU master (e.g. after re-importing All Listings so UPCs
 * are known), then refreshes the catalog, review, and counts.
 */
function RematchButton({ bookingId }: { bookingId: number }) {
  const queryClient = useQueryClient();
  const rematch = useMutation({
    mutationFn: () => api.post<BookingRematchResult>(`/api/bookings/${bookingId}/rematch`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['booking-catalog', bookingId] });
      void queryClient.invalidateQueries({ queryKey: ['booking-review', bookingId] });
    },
  });

  return (
    <div className="flex items-center gap-2">
      <Button variant="secondary" onClick={() => rematch.mutate()} disabled={rematch.isPending}>
        {rematch.isPending ? 'Re-matching…' : 'Re-match'}
      </Button>
      {rematch.isSuccess && (
        <span className="text-xs text-muted">
          Re-matched {integer(rematch.data.updated)} undecided line
          {rematch.data.updated === 1 ? '' : 's'}.
        </span>
      )}
      {rematch.isError && (
        <span className="text-xs text-danger-text">
          {rematch.error instanceof ApiError ? rematch.error.message : 'Re-match failed.'}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

function UploadCard({ onUploaded }: { onUploaded: (id: number) => void }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState<UploadBookingResult | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return api.upload<UploadBookingResult>('/api/bookings', form);
    },
    onSuccess: (outcome) => {
      setResult(outcome);
      void queryClient.invalidateQueries({ queryKey: ['bookings'] });
      onUploaded(outcome.booking.id);
    },
  });

  function submit(files: FileList | null) {
    const file = files?.[0];
    if (file) upload.mutate(file);
  }

  return (
    <Card className="p-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          submit(e.dataTransfer.files);
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-6 text-center text-sm ${
          dragging ? 'border-brand-600 bg-sunken' : 'border-line-strong'
        }`}
      >
        <p className="text-body">Drag a supplier booking workbook (.xlsx) here, or</p>
        <Button
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
        >
          {upload.isPending ? 'Uploading…' : 'Choose a file'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => submit(e.target.files)}
        />
      </div>

      {upload.isError && (
        <div className="mt-3">
          <Alert>
            {upload.error instanceof ApiError ? upload.error.message : 'Upload failed.'}
          </Alert>
        </div>
      )}
      {result && (
        <div className="mt-3">
          <Alert tone="success">
            Loaded “{result.booking.name}” ({result.booking.bookingYear}):{' '}
            {integer(result.lineCount)} lines
            {result.warnings.length > 0 ? `, ${result.warnings.length} note(s)` : ''}.
          </Alert>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

const MATCH_BADGE: Record<BookingMatchState, { label: string; className: string }> = {
  confirmed: { label: 'Confirmed', className: 'bg-ok-bg text-ok-text' },
  no_listing: { label: 'No listing', className: 'bg-sunken text-muted' },
  proposed_exact: { label: 'UPC/exact', className: 'bg-brand-600/15 text-brand-600' },
  proposed_likely: { label: 'Likely', className: 'bg-brand-600/10 text-brand-600' },
  proposed_weak: { label: 'Weak', className: 'bg-warn-bg text-warn-text' },
  unmatched: { label: 'Unmatched', className: 'bg-danger-bg text-danger-text' },
};

function MatchBadge({ state }: { state: BookingMatchState }) {
  const badge = MATCH_BADGE[state];
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
}

function CatalogPanel({ bookingId }: { bookingId: number }) {
  const [filters, setFilters] = useState({
    category: '',
    window: '',
    availability: '',
    matchState: '',
  });

  const catalog = useQuery({
    queryKey: ['booking-catalog', bookingId, filters],
    queryFn: () =>
      api.get<{ rows: BookingCatalogRow[] }>(
        `/api/bookings/${bookingId}/catalog${queryString(filters)}`,
      ),
  });

  const rows = catalog.data?.rows ?? [];

  return (
    <Card>
      <div className="flex flex-wrap items-end gap-3 border-b border-line p-3">
        <FilterSelect
          label="Category"
          value={filters.category}
          options={CATEGORY_LABELS}
          onChange={(v) => setFilters((f) => ({ ...f, category: v }))}
        />
        <FilterSelect
          label="Window"
          value={filters.window}
          options={WINDOW_LABELS}
          onChange={(v) => setFilters((f) => ({ ...f, window: v }))}
        />
        <FilterSelect
          label="Availability"
          value={filters.availability}
          options={AVAILABILITY_LABELS}
          onChange={(v) => setFilters((f) => ({ ...f, availability: v }))}
        />
        <FilterSelect
          label="Match state"
          value={filters.matchState}
          options={{
            confirmed: 'Confirmed',
            proposed: 'Proposed',
            unmatched: 'Unmatched',
            no_listing: 'No listing',
          }}
          onChange={(v) => setFilters((f) => ({ ...f, matchState: v }))}
        />
        <p className="ml-auto text-xs text-muted">{integer(rows.length)} lines</p>
      </div>

      {catalog.isPending && (
        <div className="p-4">
          <Spinner label="Loading catalog" />
        </div>
      )}
      {catalog.isError && (
        <div className="p-4">
          <Alert>{catalog.error.message}</Alert>
        </div>
      )}
      {catalog.data && (
        <TableScroll>
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 font-medium">Window</th>
                <th className="px-3 py-2 font-medium">Item code</th>
                <th className="px-3 py-2 font-medium">Model</th>
                <th className="px-3 py-2 font-medium">Color</th>
                <th className="px-3 py-2 font-medium">Size</th>
                <th className="px-3 py-2 font-medium">Availability</th>
                <th className="px-3 py-2 text-right font-medium">MAP</th>
                <th className="px-3 py-2 text-right font-medium">Cost</th>
                <th className="px-3 py-2 font-medium">Match</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                // Only a later-window alternate is muted; a racquet with a demo
                // twin is shown normally with a badge rather than dimmed.
                <tr key={r.id} className={r.alternate ? 'text-muted' : ''}>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {CATEGORY_LABELS[r.category] ?? r.category}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {WINDOW_LABELS[r.window] ?? r.window}
                    {r.prebook && <span className="ml-1 text-xs text-warn-text">pre-book</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">{r.itemCode}</td>
                  <td className="px-3 py-1.5">
                    {r.model}
                    {r.packageType && (
                      <span className="ml-1 text-xs text-muted">({r.packageType})</span>
                    )}
                    {r.superseded && (
                      <span className="ml-1 rounded bg-brand-600/10 px-1.5 py-0.5 text-xs text-brand-600">
                        demo available
                      </span>
                    )}
                    {r.demoTwinAvailable === false && (
                      <span className="ml-1 text-xs text-muted">· demo not available</span>
                    )}
                    {r.alternate && <span className="ml-1 text-xs">· later window</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">{r.color ?? '—'}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {r.sizeCanonical ?? r.sizeLabel ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {AVAILABILITY_LABELS[r.availability] ?? r.availability}
                  </td>
                  <td className="tabular whitespace-nowrap px-3 py-1.5 text-right">
                    {money(r.map)}
                  </td>
                  <td className="tabular whitespace-nowrap px-3 py-1.5 text-right">
                    {money(r.unitCost)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <MatchBadge state={r.matchState} />
                      {r.sku && (
                        <>
                          <ProductThumb asin={r.sku.asin} title={r.sku.title} />
                          <span className="text-xs text-muted">{r.sku.sellerSku}</span>
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-6 text-center text-sm text-muted">
                    No lines match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Card>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted">
      <span>{label}</span>
      <Select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-40"
      >
        <option value="">All</option>
        {Object.entries(options).map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </Select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Match review
// ---------------------------------------------------------------------------

function ReviewPanel({ bookingId, canWrite }: { bookingId: number; canWrite: boolean }) {
  const review = useQuery({
    queryKey: ['booking-review', bookingId],
    queryFn: () => api.get<BookingReviewResult>(`/api/bookings/${bookingId}/review`),
  });

  const counts = review.data?.counts;

  return (
    <div className="space-y-3">
      {counts && (
        <div className="flex flex-wrap gap-3 text-xs text-muted">
          <span className="font-medium text-body">{integer(counts.remaining)} left to review</span>
          <span>· {integer(counts.confirmed)} confirmed</span>
          <span>· {integer(counts.noListing)} no listing</span>
          <span>· {integer(counts.total)} lines total</span>
        </div>
      )}

      {review.isPending && <Spinner label="Loading review" />}
      {review.isError && <Alert>{review.error.message}</Alert>}

      {review.data && review.data.rows.length === 0 && (
        <Card className="p-6">
          <p className="text-sm text-muted">Everything is decided. Nothing left to review.</p>
        </Card>
      )}

      {review.data?.rows.map((row) => (
        <ReviewRowItem key={row.id} bookingId={bookingId} row={row} canWrite={canWrite} />
      ))}
    </div>
  );
}

function ReviewRowItem({
  bookingId,
  row,
  canWrite,
}: {
  bookingId: number;
  row: BookingReviewRow;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const decide = useMutation({
    mutationFn: (decision: BookingMatchDecision) =>
      api.post(`/api/bookings/${bookingId}/matches`, decision),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['booking-review', bookingId] });
      void queryClient.invalidateQueries({ queryKey: ['booking-catalog', bookingId] });
    },
  });

  // The per-pair "order from the racquet tab" lever, stored on the demo's key.
  const prefer = useMutation({
    mutationFn: (value: boolean | null) =>
      api.post(`/api/bookings/${bookingId}/prefer-racquet`, {
        itemCode: row.itemCode,
        sizeLabel: row.sizeLabel,
        discriminator: row.matchDiscriminator,
        preferRacquet: value,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['booking-review', bookingId] });
      void queryClient.invalidateQueries({ queryKey: ['booking-catalog', bookingId] });
    },
  });

  function confirm(skuId: number) {
    decide.mutate({
      itemCode: row.itemCode,
      sizeLabel: row.sizeLabel,
      discriminator: row.matchDiscriminator,
      skuId,
    });
  }
  function noListing() {
    decide.mutate({
      itemCode: row.itemCode,
      sizeLabel: row.sizeLabel,
      discriminator: row.matchDiscriminator,
      noListing: true,
    });
  }

  return (
    <Card className="p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {/* A thumb only for a matched/proposed line (one tied to a SKU/ASIN). */}
          {row.sku && <ProductThumb asin={row.sku.asin} title={row.sku.title} />}
          <div>
            <span className="font-medium text-body">{row.model}</span>
            {row.color && <span className="ml-1 text-sm text-muted">{row.color}</span>}
            {row.sizeCanonical && (
              <span className="ml-1 text-sm text-muted">· {row.sizeCanonical}</span>
            )}
            <span className="ml-2 font-mono text-xs text-muted">{row.itemCode}</span>
          </div>
        </div>
        <MatchBadge state={row.matchState} />
      </div>

      {canWrite ? (
        <div className="mt-2 space-y-2">
          {row.candidates.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {row.candidates.map((c) => (
                <Button
                  key={c.skuId}
                  variant="secondary"
                  disabled={decide.isPending}
                  onClick={() => confirm(c.skuId)}
                >
                  Use {c.sellerSku} <span className="text-xs text-muted">({c.confidence})</span>
                </Button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted">No suggested SKU — search for one below.</p>
          )}

          <SkuPicker
            value={search}
            onChange={setSearch}
            onPick={(skuId) => confirm(skuId)}
            disabled={decide.isPending}
          />

          <div>
            <Button variant="ghost" onClick={noListing} disabled={decide.isPending}>
              Mark as no listing
            </Button>
          </div>
          {row.category === 'demo_racquet' && (
            <div className="space-y-1">
              <label
                className="flex items-center gap-2 text-sm text-muted"
                title="For a clean racquet/demo pair: order the racquet tab's line instead of this demo, even when the demo is orderable. Marking the demo &ldquo;no listing&rdquo; alone does not change which cell is ordered when the racquet is confirmed — this is the lever."
              >
                <input
                  type="checkbox"
                  checked={row.preferRacquet === true}
                  disabled={prefer.isPending}
                  onChange={(e) => prefer.mutate(e.target.checked ? true : null)}
                />
                Order this pair from the racquet tab (not the demo)
              </label>
              {row.preferRacquetFallback && (
                <p className="text-xs text-warn-text">
                  Racquet not orderable — the demo is ordered instead.
                </p>
              )}
            </div>
          )}
          {(decide.isError || prefer.isError) && (
            <Alert>
              {decide.error instanceof ApiError
                ? decide.error.message
                : prefer.error instanceof ApiError
                  ? prefer.error.message
                  : 'Could not save.'}
            </Alert>
          )}
        </div>
      ) : (
        <p className="mt-1 text-sm text-muted">
          {row.sku ? `Proposed: ${row.sku.sellerSku}` : 'No proposed SKU.'} Sign in as an importer
          to decide.
        </p>
      )}
    </Card>
  );
}

function SkuPicker({
  value,
  onChange,
  onPick,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  onPick: (skuId: number) => void;
  disabled: boolean;
}) {
  const results = useQuery({
    queryKey: ['booking-sku-search', value],
    queryFn: () =>
      api.get<{ skus: { id: number; sellerSku: string; title: string | null }[] }>(
        `/api/bookings/skus/search${queryString({ q: value })}`,
      ),
    enabled: value.trim().length >= 2,
  });

  const skus = useMemo(() => results.data?.skus ?? [], [results.data]);

  return (
    <div className="max-w-md">
      <Input
        placeholder="Search a SKU by SKU, ASIN, or title…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
      {value.trim().length >= 2 && skus.length > 0 && (
        <ul className="mt-1 divide-y divide-line rounded-md border border-line">
          {skus.slice(0, 8).map((s) => (
            <li key={s.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onPick(s.id)}
                className="flex w-full flex-col px-3 py-1.5 text-left hover:bg-sunken disabled:opacity-50"
              >
                <span className="text-sm text-body">{s.sellerSku}</span>
                {s.title && <span className="truncate text-xs text-muted">{s.title}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
